import { Flex, type FlexProps, Text, type TextProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type ReactNode } from "react";
import { ButtonGroup } from "ui/button/button-group";

export type TextLargeProps = {
  /**
   * The color theme to use for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The relative size for the text elements. All sizes are styled to be responsive.
   */
  size: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

  /**
   * Text to display.
   */
  text: ReactNode;

  /**
   * The html element to use for the text.
   *
   * @default "p"
   */
  textAs?: TextProps["as"];

  /**
   * An optional section to display at the bottom of the element for CTA buttons, links, etc.
   * Will be wrapped in a `ButtonGroup` to space multiple elements evenly.
   */
  actions?: ReactNode;
} & FlexProps;

/**
 * Renders out left-aligned, header-style text at a large width. CTA elements can optionally be included.
 */
export function TextLarge({ theme, size, text, textAs = "p", actions, ...flexProps }: TextLargeProps) {
  let textStyle: TextProps["textStyle"];
  let maxW: FlexProps["maxW"];
  let flexGap: FlexProps["gap"] = "bigger";

  switch (size) {
    case "2xl": {
      textStyle = { base: "display3", md: "display1" };
      break;
    }

    case "xl": {
      textStyle = { base: "headline1", md: "display2" };
      break;
    }

    case "lg": {
      textStyle = { base: "headline2", md: "display3" };
      break;
    }

    case "md": {
      flexGap = "big";
      maxW = "1100px";
      textStyle = { base: "headline2", md: "headline1" };
      break;
    }

    case "sm": {
      flexGap = "big";
      maxW = "860px";
      textStyle = { base: "headline3", md: "headline2" };
      break;
    }

    case "xs": {
      flexGap = "big";
      maxW = "640px";
      textStyle = "headline3";
      break;
    }

    // No default
  }

  return (
    <Flex flexDir="column" align="start" color={`brand.text.${theme}`} gap={flexGap} maxW={maxW} {...flexProps}>
      <Text as={textAs} textStyle={textStyle}>
        {text}
      </Text>
      {actions && <ButtonGroup>{actions}</ButtonGroup>}
    </Flex>
  );
}
